import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import {
  Box,
  CircularProgress,
  Alert,
  Snackbar,
  Typography,
  Button,
} from '@mui/material';
import { Refresh } from '@mui/icons-material';
import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
} from 'react-query';
import {
  messagingService,
  QK,
  wsService,
} from '../../api/services/messagingService';
import authService from '../../api/services/authService';
import { MessageChat, MessageSidebar } from '../../components/Messaging/ChatMessaging';

const PAGE_SIZE = 10;

const MessageSystem = () => {
  const user = useMemo(() => authService.getUser(), []);
  const token = useMemo(() => authService.getToken(), []);
  const queryClient = useQueryClient();

  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [showDefaultGroup, setShowDefaultGroup] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const [toast, setToast] = useState({
    open: false,
    message: '',
    severity: 'info',
  });

  const isSendingRef = useRef(false);
  const messageIdsRef = useRef(new Set());
  const wsConnectedRef = useRef(false);
  const connectionInitRef = useRef(false);
  const currentConvRef = useRef(null);
  const [newMessageIds, setNewMessageIds] = useState(new Set());
  const wsEventListenersRef = useRef([]);
  const sendTimeoutRef = useRef(null);

  const showToast = useCallback((message, severity = 'info') => {
    setToast({ open: true, message, severity });
  }, []);

  const handleToastClose = useCallback(() => {
    setToast((prev) => ({ ...prev, open: false }));
  }, []);

  /* ---------- Queries ---------- */

  const { data: defaultConversation } = useQuery({
    queryKey: ['defaultConversation'],
    queryFn: () => messagingService.getDefaultConversation(),
    enabled: !!token,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (defaultConversation && showDefaultGroup) {
      setSelectedConversation(defaultConversation);
      setShowDefaultGroup(false);
    }
  }, [defaultConversation, showDefaultGroup]);

  const {
    data: conversationsData,
    isLoading: conversationsLoading,
    error: conversationsError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch: refetchConversations,
  } = useInfiniteQuery({
    queryKey: [QK.conversations],
    queryFn: ({ pageParam = 1 }) =>
      messagingService.getConversations(pageParam, PAGE_SIZE),
    getNextPageParam: (lastPage, pages) => {
      if (lastPage.next) {
        const urlParams = new URLSearchParams(lastPage.next.split('?')[1]);
        return parseInt(urlParams.get('page')) || pages.length + 1;
      }
      return undefined;
    },
    staleTime: 5 * 60 * 1000,
  });

  const { data: messagesData, isLoading: messagesLoading } = useQuery({
    queryKey: [QK.messages, selectedConversation?.id],
    queryFn: () =>
      messagingService.getConversationMessages(selectedConversation?.id),
    enabled: !!selectedConversation?.id,
    staleTime: 2 * 60 * 1000,
  });

  /* ---------- Mutations ---------- */

  const createConversationMutation = useMutation(
    messagingService.createConversation,
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries([QK.conversations]);
        setSelectedConversation(data);
      },
      onError: () => showToast('Failed to create conversation', 'error'),
    }
  );

  const markReadMutation = useMutation(messagingService.markMessageRead, {
    onSuccess: () =>
      queryClient.invalidateQueries([
        QK.messages,
        selectedConversation?.id,
      ]),
  });

  /* ---------- WebSocket ---------- */

  const setupWebSocket = useCallback(() => {
    const convId = selectedConversation?.id;

    if (!convId || !token) return;
    if (wsConnectedRef.current && currentConvRef.current === convId) return;
    if (connectionInitRef.current) return;

    wsService.disconnect();
    wsEventListenersRef.current.forEach(({ event, handler }) => {
      wsService.off(event, handler);
    });
    wsEventListenersRef.current = [];
    wsConnectedRef.current = false;

    wsService.connect(convId, token);
    wsConnectedRef.current = true;
    currentConvRef.current = convId;
    connectionInitRef.current = true;

    const handleIncomingMessage = (data) => {
      const message = data.data || data;
      if (!message.id || messageIdsRef.current.has(message.id)) return;

      messageIdsRef.current.add(message.id);

      if (message.conversation_id === convId) {
        setMessages((prev) => {
          const exists = prev.some((m) => m.id === message.id);
          if (exists) return prev;
          const filtered = prev.filter((m) => m.id !== message.id);
          const withoutTemp = filtered.filter(
            (m) => !(typeof m.id === 'string' && m.id.startsWith('temp_'))
          );
          return [...withoutTemp, message].sort(
            (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
          );
        });
      }

      setNewMessageIds((prev) => new Set(prev).add(message.conversation_id));
      queryClient.invalidateQueries([QK.conversations]);
    };

    const handlers = {
      connected: () => {
        setIsConnected(true);
      },
      new_message: handleIncomingMessage,
      message: handleIncomingMessage,
      read_receipt: (data) => {
        const receiptData = data.data || data;
        const messageId = receiptData.message_id || receiptData.id;
        const userId = receiptData.user_id || receiptData.userId;
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === messageId
              ? {
                  ...msg,
                  read_by: [...(msg.read_by || []), { id: userId }],
                  status: 'READ',
                }
              : msg
          )
        );
      },
      delivery_receipt: (data) => {
        const receiptData = data.data || data;
        const messageId = receiptData.message_id || receiptData.id;
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === messageId
              ? { ...msg, delivered: true, status: 'DELIVERED' }
              : msg
          )
        );
      },
      disconnected: () => {
        setIsConnected(false);
        wsConnectedRef.current = false;
        connectionInitRef.current = false;
      },
      error: (error) => {
        console.error('WebSocket error:', error);
        setIsConnected(false);
        wsConnectedRef.current = false;
        showToast(error?.message || 'Connection error', 'error');
      },
      reconnect_failed: () => {
        showToast('Failed to reconnect. Please refresh.', 'error');
        setIsConnected(false);
        wsConnectedRef.current = false;
        connectionInitRef.current = false;
      },
    };

    Object.entries(handlers).forEach(([event, handler]) => {
      wsService.on(event, handler);
      wsEventListenersRef.current.push({ event, handler });
    });
  }, [selectedConversation?.id, token, queryClient, showToast]);

  useEffect(() => {
    if (currentConvRef.current !== selectedConversation?.id) {
      connectionInitRef.current = false;
      wsConnectedRef.current = false;
    }

    if (selectedConversation?.id && token) {
      setupWebSocket();
    }

    return () => {
      if (selectedConversation?.id) {
        wsEventListenersRef.current.forEach(({ event, handler }) => {
          wsService.off(event, handler);
        });
        wsEventListenersRef.current = [];
        wsService.disconnect();
        wsConnectedRef.current = false;
        connectionInitRef.current = false;
        setIsConnected(false);
      }
    };
  }, [selectedConversation?.id, token, setupWebSocket]);

  useEffect(() => {
    if (messagesData) {
      const sorted = [...messagesData].sort(
        (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
      );
      setMessages(sorted);
      sorted.forEach((msg) => messageIdsRef.current.add(msg.id));
    }
  }, [messagesData]);

  /* ---------- Send ---------- */

  const handleSend = useCallback(() => {
    if (isSendingRef.current) return;

    const content = newMessage?.trim();
    if (!content) {
      showToast('Please enter a message', 'warning');
      return;
    }
    if (!selectedConversation) {
      showToast('No conversation selected', 'warning');
      return;
    }
    if (!isConnected) {
      showToast('Not connected to server', 'warning');
      return;
    }

    isSendingRef.current = true;

    const tempId = `temp_${Date.now()}_${Math.random()
      .toString(36)
      .substr(2, 6)}`;

    const optimisticMessage = {
      id: tempId,
      content,
      sender: {
        id: user?.id,
        username: user?.username,
        first_name: user?.first_name,
        last_name: user?.last_name,
      },
      sender_id: user?.id,
      timestamp: new Date().toISOString(),
      status: 'SENDING',
      read_by: [],
      delivered: false,
    };

    setMessages((prev) => {
      const exists = prev.some((m) => m.id === tempId);
      if (exists) return prev;
      return [...prev, optimisticMessage];
    });

    setNewMessage('');

    try {
      wsService.sendMessage(content);
    } catch (error) {
      console.error('Send error:', error);
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === tempId ? { ...msg, status: 'FAILED' } : msg
        )
      );
      isSendingRef.current = false;
      showToast('Failed to send message', 'error');
      return;
    }

    if (sendTimeoutRef.current) clearTimeout(sendTimeoutRef.current);
    sendTimeoutRef.current = setTimeout(() => {
      isSendingRef.current = false;
      sendTimeoutRef.current = null;
    }, 3000);
  }, [newMessage, selectedConversation, isConnected, user, showToast]);

  useEffect(() => {
    return () => {
      if (sendTimeoutRef.current) {
        clearTimeout(sendTimeoutRef.current);
        sendTimeoutRef.current = null;
      }
    };
  }, []);

  const handleStartNewConversation = useCallback(
    (employee) => {
      if (!employee) return;

      const existingConversation = conversationsData?.pages
        ?.flatMap((page) => page.results || [])
        .find((conv) => {
          if (conv.is_group) return false;
          const otherParticipants =
            conv.participants?.filter((p) => p.id !== user?.id) || [];
          return (
            otherParticipants.length === 1 &&
            otherParticipants[0].id === employee.user_id
          );
        });

      if (existingConversation) {
        setSelectedConversation(existingConversation);
        return;
      }

      createConversationMutation.mutate({
        participant_ids: [employee.user_id, user.id],
        is_group: false,
      });
    },
    [conversationsData, user?.id, createConversationMutation]
  );

  /* ---------- Loading / Error ---------- */

  if (conversationsLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          minHeight: 400,
          bgcolor: '#F8FAFC',
          gap: 2,
        }}
      >
        <CircularProgress size={36} sx={{ color: '#1570EF' }} />
        <Typography
          sx={{ fontSize: 13.5, color: '#64748B', fontWeight: 500 }}
        >
          Loading conversations...
        </Typography>
      </Box>
    );
  }

  if (conversationsError) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          minHeight: 400,
          bgcolor: '#F8FAFC',
          p: 3,
          gap: 2,
        }}
      >
        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: '50%',
            bgcolor: '#FEF2F2',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Alert sx={{ color: '#EF4444', fontSize: 36 }} />
        </Box>
        <Typography
          sx={{
            fontSize: 16,
            fontWeight: 700,
            color: '#0F172A',
            textAlign: 'center',
          }}
        >
          Failed to load conversations
        </Typography>
        <Typography
          sx={{
            fontSize: 13.5,
            color: '#64748B',
            textAlign: 'center',
            maxWidth: 320,
          }}
        >
          There was a problem loading your messages. Please check your
          connection and try again.
        </Typography>
        <Button
          variant="contained"
          startIcon={<Refresh />}
          onClick={() => refetchConversations()}
          sx={{
            mt: 1,
            bgcolor: '#1570EF',
            textTransform: 'none',
            fontSize: 13.5,
            fontWeight: 600,
            borderRadius: 2,
            px: 2.5,
            py: 1,
            boxShadow: 'none',
            '&:hover': { bgcolor: '#0E5FD9', boxShadow: 'none' },
          }}
        >
          Try again
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        height: '90vh',
        minHeight: 0,
        display: 'flex',
        bgcolor: '#F8FAFC',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <MessageSidebar
        conversations={conversationsData}
        selectedId={selectedConversation?.id}
        userId={user?.id}
        onSelect={setSelectedConversation}
        onLoadMore={fetchNextPage}
        hasMore={hasNextPage}
        loadingMore={isFetchingNextPage}
        onStartNewConversation={handleStartNewConversation}
        newMessageIds={newMessageIds}
        setNewMessageIds={setNewMessageIds}
      />

      <MessageChat
        conversation={selectedConversation}
        messages={messages}
        userId={user?.id}
        onSend={handleSend}
        newMessage={newMessage}
        setNewMessage={setNewMessage}
        isSending={isSendingRef.current}
        isLoading={messagesLoading}
        onBack={() => {
          setSelectedConversation(null);
          connectionInitRef.current = false;
          wsConnectedRef.current = false;
          currentConvRef.current = null;
        }}
        onMarkAsRead={(id) => markReadMutation.mutate(id)}
        isConnected={isConnected}
      />

      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={handleToastClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ bottom: { xs: 16, md: 24 } }}
      >
        <Alert
          severity={toast.severity}
          onClose={handleToastClose}
          variant="filled"
          sx={{
            borderRadius: 2,
            fontSize: 13.5,
            fontWeight: 500,
            boxShadow: '0 8px 24px rgba(15, 23, 42, 0.15)',
          }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MessageSystem;